import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import { reportsAPI } from '../../services/api';
import PageTitle from '../shared/PageTitle';
import '../../styles/Dashboard.css';

// Export Section Component for data export cards
const ExportSection = ({ title, description, dataType, icon, color, exportLoading, handleExportData, canExport }) => {
  const isLoadingCSV = exportLoading[`${dataType}_csv`];
  const isLoadingPDF = exportLoading[`${dataType}_pdf`];

  return (
    <div className="col-md-12 col-lg-6 mb-2">
      <div className="card export-card h-100 compact-export-card">
        <div className="card-body p-3">
          <div className="d-flex align-items-start">
            <div className={`export-icon compact-export-icon bg-${color} text-white me-2`}>
              <i className={icon}></i>
            </div>
            <div className="flex-grow-1">
              <h6 className="card-title compact-title mb-1 fw-bold">{title}</h6>
              <p className="text-muted compact-description mb-2 small">{description}</p>
              
              {canExport ? (
                <div className="d-flex gap-1" role="group">
                  <button
                    type="button"
                    className={`btn btn-outline-${color} btn-sm flex-fill compact-btn ${isLoadingCSV ? 'disabled' : ''}`}
                    onClick={() => handleExportData(dataType, 'csv')}
                    disabled={isLoadingCSV}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    {isLoadingCSV ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '0.75rem', height: '0.75rem' }}></span>
                        <span className="d-none d-md-inline">Loading...</span>
                        <span className="d-inline d-md-none">...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-csv me-1"></i>
                        CSV
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-${color} btn-sm flex-fill compact-btn ${isLoadingPDF ? 'disabled' : ''}`}
                    onClick={() => handleExportData(dataType, 'pdf')}
                    disabled={isLoadingPDF}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    {isLoadingPDF ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" style={{ width: '0.75rem', height: '0.75rem' }}></span>
                        <span className="d-none d-md-inline">Loading...</span>
                        <span className="d-inline d-md-none">...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-pdf me-1"></i>
                        PDF
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <span className="badge bg-secondary compact-badge small">
                    <i className="fas fa-lock me-1"></i>
                    <span className="d-none d-lg-inline">Admin Required</span>
                    <span className="d-inline d-lg-none">Admin Only</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const { settings, formatCurrency } = useSettings();
  const { currentUser } = useContext(AuthContext);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeReport, setActiveReport] = useState('occupancy');
  const [dateRange, setDateRange] = useState('month');
  const [exportLoading, setExportLoading] = useState({});
  const [exportMessage, setExportMessage] = useState('');
  
  // Get dwelling unit names
  const unitPlural = formatDwellingUnit(settings, null, true);
  
  // Check if user has export permissions
  const canExport = currentUser && ['admin', 'superadmin'].includes(currentUser.role);
  
  // Load report data
  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        // Use the reportsAPI to fetch data
        const response = await reportsAPI.getReportData(activeReport, dateRange);
        setReportData(response.data);
        setError(null);
      } catch (err) {
        console.error(`Error fetching ${activeReport} report data:`, err);
        setError(`Failed to load ${activeReport} report data. Please check the API connection and try again.`);
        // Ensure reportData is cleared or set to a state that indicates failure
        setReportData(null); 
      } finally {
        setLoading(false);
      }
    };
    
    fetchReportData();
  }, [activeReport, dateRange]);
  
  // Handle export functionality
  const handleExportData = async (dataType, format = 'csv') => {
    if (!canExport) {
      setExportMessage('You do not have permission to export data.');
      setTimeout(() => setExportMessage(''), 3000);
      return;
    }
    
    const exportKey = `${dataType}_${format}`;
    setExportLoading(prev => ({ ...prev, [exportKey]: true }));
    setExportMessage('');
    
    try {
      let exportPromise;
      let filename;
      
      switch (dataType) {
        case 'visitors':
          exportPromise = reportsAPI.exportData.visitors(format, { range: dateRange });
          filename = 'visitors_data';
          break;
        case 'financials':
          exportPromise = reportsAPI.exportData.financials(format, { range: dateRange });
          filename = 'financials_with_transactions';
          break;
        case 'units':
          exportPromise = reportsAPI.exportData.units(format, { range: dateRange });
          filename = 'units_data';
          break;
        case 'complaints':
          exportPromise = reportsAPI.exportData.complaints(format, { range: dateRange });
          filename = 'complaints_data';
          break;
        case 'directory':
          exportPromise = reportsAPI.exportData.directory(format, { range: dateRange });
          filename = 'directory_residents_staff';
          break;
        case 'users':
          exportPromise = reportsAPI.exportData.users(format, { range: dateRange });
          filename = 'users_data';
          break;
        default:
          throw new Error('Invalid data type');
      }
      
      const result = await reportsAPI.handleExportDownload(exportPromise, filename, format);
      setExportMessage(result.message);
      setTimeout(() => setExportMessage(''), 5000);
      
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage(error.message || 'Export failed. Please try again.');
      setTimeout(() => setExportMessage(''), 5000);
    } finally {
      setExportLoading(prev => ({ ...prev, [exportKey]: false }));
    }
  };
  
  // Handle report download (PDF only)
  const handleDownloadReport = (format) => {
    setLoading(true);
    try {
      console.log(`Downloading ${activeReport} report in ${format} format...`);
      
      reportsAPI.downloadReport(activeReport, format, dateRange)
        .then(response => {
          console.log('Download response received:', response);
          
          // Create a URL for the PDF blob
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          
          // Create a link element
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${activeReport}_report_${new Date().toISOString().slice(0, 10)}.pdf`);
          
          // Append link to body, click it, then remove it
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the URL object
          window.URL.revokeObjectURL(url);
          setLoading(false);
          
          console.log('PDF report downloaded successfully');
        })
        .catch(err => {
          console.error(`Error downloading ${activeReport} report:`, err);
          console.error('Error details:', err.response?.data || err.message);
          
          // Check if it's an authentication error
          if (err.response?.status === 401 || err.response?.status === 403) {
            alert('You need to be logged in as an admin to download reports.');
          } else if (err.response?.status === 500) {
            alert('Server error while generating the report. Please try again later.');
          } else {
            alert('Failed to download PDF report. Please try again later.');
          }
        });
    } catch (err) {
      console.error('Download error:', err);
      alert(`Download functionality is currently unavailable: ${err.message}`);
    }
  };
  
  // Helper function to render content based on active report type
  const renderReportContent = () => {
    if (!reportData) {
      return (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          No report data available. Please try another report type or date range.
        </div>
      );
    }
    
    switch (activeReport) {
      case 'occupancy':
        return renderOccupancyReport();
      case 'financial':
        return renderFinancialReport();
      case 'complaints':
        return renderComplaintsReport();
      case 'visitors':
        return renderVisitorsReport();
      default:
        return (
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please select a valid report type.
          </div>
        );
    }
  };
  
  // Render occupancy report content
  const renderOccupancyReport = () => {
    if (!reportData) return null;
    
    const { 
      totalUnits = 0, 
      occupiedUnits = 0, 
      vacantUnits = 0,
      occupancyRate = 0,
      occupancyByType = [],
      monthlyTrend = []
    } = reportData;
    
    return (
      <>
        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-6 col-md-12 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Total {unitPlural}</h6>
                <h2 className="card-text">{totalUnits}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-md-12 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Occupied {unitPlural}</h6>
                <h2 className="card-text text-success">{occupiedUnits}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-md-12 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Vacant {unitPlural}</h6>
                <h2 className="card-text text-danger">{vacantUnits}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-lg-6 col-md-12 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Occupancy Rate</h6>
                <h2 className="card-text text-primary">{occupancyRate}%</h2>
              </div>
            </div>
          </div>
        </div>
        
        {/* Occupancy by Unit Type */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Occupancy by Unit Type</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Unit Type</th>
                        <th>Total Units</th>
                        <th>Occupied Units</th>
                        <th>Vacant Units</th>
                        <th>Occupancy Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(occupancyByType || []).map((type, index) => (
                        <tr key={index}>
                          <td>{type.type}</td>
                          <td>{type.total}</td>
                          <td className="text-success">{type.occupied}</td>
                          <td className="text-danger">{type.vacant}</td>
                          <td>{Math.round((type.occupied / type.total) * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Monthly Trend Chart (Placeholder) */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Monthly Occupancy Trend</h5>
              </div>
              <div className="card-body">
                <div className="chart-container" style={{ height: '300px' }}>
                  {/* In a real implementation, this would be an actual chart component */}
                  <div className="placeholder-chart">
                    <div className="d-flex justify-content-between mb-4">
                      {(monthlyTrend || []).map((item, index) => (
                        <div key={index} className="text-center">
                          <div className="mb-2">{item.month}</div>
                          <div 
                            className="bg-primary rounded" 
                            style={{ 
                              height: `${item.rate * 2}px`, 
                              width: '30px'
                            }}
                          ></div>
                          <div className="mt-2">{item.rate}%</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-muted">Monthly Occupancy Rate (%)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  // Render financial report content
  const renderFinancialReport = () => {
    if (!reportData) return null;
    
    const { 
      totalIncome = 0, 
      totalExpenses = 0, 
      // balance = 0, 
      openingBalance = 0,
      closingBalance = 0,
      // pendingPayments = 0,
      transactions = [],
      revenueByCategory = [],
      expensesByCategory = [],
      // monthlyFinancials = []
    } = reportData;

    // Use actual transactions from backend or create bank statement data from categories if no transactions
    const createBankStatementData = () => {
      if (transactions && transactions.length > 0) {
        // Use actual transactions from backend
        return transactions.map(transaction => ({
          date: new Date(transaction.date),
          description: `${transaction.category} - ${transaction.description || 'Transaction'}`,
          type: transaction.type === 'payment' ? 'credit' : 'debit',
          amount: transaction.amount,
          balance: transaction.running_balance
        }));
      } else {
        // If no transactions in the period, show opening balance row
        if (openingBalance !== 0) {
          return [{
            date: new Date(),
            description: `Opening Balance (${dateRange.charAt(0).toUpperCase() + dateRange.slice(1)} Period)`,
            type: 'balance',
            amount: 0,
            balance: openingBalance
          }];
        }
        return [];
      }
    };

    const bankTransactions = createBankStatementData();
    
    return (
      <>
        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Opening Balance</h6>
                <h2 className={`card-text ${openingBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatCurrency(openingBalance)}
                </h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Period Income</h6>
                <h2 className="card-text text-success">{formatCurrency(totalIncome)}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Period Expenses</h6>
                <h2 className="card-text text-danger">{formatCurrency(totalExpenses)}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Closing Balance</h6>
                <h2 className={`card-text ${closingBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                  {formatCurrency(closingBalance)}
                </h2>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bank Statement Format */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-university me-2"></i>
                  Financial Statement - Bank Statement View
                </h5>
                <small>Transaction history with running balance for {dateRange.charAt(0).toUpperCase() + dateRange.slice(1)} period</small>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th className="text-center" style={{ width: '120px' }}>Date</th>
                        <th>Description</th>
                        <th className="text-end" style={{ width: '120px' }}>Debit</th>
                        <th className="text-end" style={{ width: '120px' }}>Credit</th>
                        <th className="text-end" style={{ width: '140px' }}>Running Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bankTransactions.length > 0 ? (
                        bankTransactions.map((transaction, index) => (
                          <tr key={index} className={transaction.type === 'credit' ? 'table-light' : ''}>
                            <td className="text-center">
                              <small>{new Date(transaction.date).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {transaction.type === 'balance' ? (
                                  <i className="fas fa-balance-scale text-info me-2"></i>
                                ) : (
                                  <i className={`fas ${transaction.type === 'credit' ? 'fa-arrow-down text-success' : 'fa-arrow-up text-danger'} me-2`}></i>
                                )}
                                <span>{transaction.description}</span>
                              </div>
                            </td>
                            <td className="text-end">
                              {transaction.type === 'debit' ? (
                                <span className="text-danger fw-bold">
                                  {formatCurrency(transaction.amount)}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="text-end">
                              {transaction.type === 'credit' ? (
                                <span className="text-success fw-bold">
                                  {formatCurrency(transaction.amount)}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="text-end">
                              <span className={`fw-bold ${transaction.balance >= 0 ? 'text-primary' : 'text-danger'}`}>
                                {formatCurrency(transaction.balance)}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4">
                            <i className="fas fa-receipt fa-3x mb-3 d-block"></i>
                            No transactions found for the selected period
                            {openingBalance !== 0 && (
                              <div className="mt-2">
                                <small>Current Balance: <strong>{formatCurrency(openingBalance)}</strong></small>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="table-secondary">
                      <tr>
                        <th colSpan="2" className="text-end">Period Summary:</th>
                        <th className="text-end text-danger">{formatCurrency(totalExpenses)}</th>
                        <th className="text-end text-success">{formatCurrency(totalIncome)}</th>
                        <th className="text-end">
                          <span className={`fw-bold ${closingBalance >= 0 ? 'text-white' : 'text-danger'}`}>
                            {formatCurrency(closingBalance)}
                          </span>
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Category Breakdown */}
        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Income by Category</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(revenueByCategory || []).map((category, index) => (
                        <tr key={index}>
                          <td>{category.category}</td>
                          <td className="text-end">{formatCurrency(category.amount)}</td>
                          <td className="text-end">
                            <span className="badge bg-success">
                              {totalIncome > 0 ? Math.round((category.amount / totalIncome) * 100) : 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {revenueByCategory.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">No income transactions in this period</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Expenses by Category</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(expensesByCategory || []).map((category, index) => (
                        <tr key={index}>
                          <td>{category.category}</td>
                          <td className="text-end">{formatCurrency(category.amount)}</td>
                          <td className="text-end">
                            <span className="badge bg-danger">
                              {totalExpenses > 0 ? Math.round((category.amount / totalExpenses) * 100) : 0}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {expensesByCategory.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">No expense transactions in this period</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  // Render complaints report content
  const renderComplaintsReport = () => {
    if (!reportData) return null;
    
    const { 
      totalComplaints = 0, 
      openComplaints = 0, 
      inProgressComplaints = 0, 
      resolvedComplaints = 0,
      resolutionRate = 0,
      averageResolutionTime = 'N/A',
      complaintsByCategory = [],
      monthlyComplaints = []
    } = reportData;
    
    return (
      <>
        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Total Complaints</h6>
                <h2 className="card-text">{totalComplaints}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Open Complaints</h6>
                <h2 className="card-text text-danger">{openComplaints}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Resolution Rate</h6>
                <h2 className="card-text text-primary">{resolutionRate}%</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Avg Resolution Time</h6>
                <h2 className="card-text text-warning">{averageResolutionTime}</h2>
              </div>
            </div>
          </div>
        </div>
        
        {/* Complaints Status Chart */}
        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Complaints Status</h5>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-around text-center">
                  <div>
                    <div className="display-4 text-danger">{openComplaints}</div>
                    <div>Open</div>
                  </div>
                  <div>
                    <div className="display-4 text-warning">{inProgressComplaints}</div>
                    <div>In Progress</div>
                  </div>
                  <div>
                    <div className="display-4 text-success">{resolvedComplaints}</div>
                    <div>Resolved</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Complaints by Category</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Count</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(complaintsByCategory || []).map((category, index) => (
                        <tr key={index}>
                          <td>{category.category}</td>
                          <td>{category.count}</td>
                          <td>
                            {Math.round((category.count / totalComplaints) * 100)}%
                            <div className="progress mt-1" style={{ height: '6px' }}>
                              <div 
                                className="progress-bar bg-primary" 
                                style={{ width: `${(category.count / totalComplaints) * 100}%` }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Monthly Trend Chart (Placeholder) */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Monthly Complaints Trend</h5>
              </div>
              <div className="card-body">
                <div className="chart-container" style={{ height: '300px' }}>
                  {/* In a real implementation, this would be an actual chart component */}
                  <div className="placeholder-chart">
                    <div className="d-flex justify-content-between mb-4">
                      {(monthlyComplaints || []).map((item, index) => (
                        <div key={index} className="text-center" style={{ width: '80px' }}>
                          <div className="mb-2">{item.month}</div>
                          <div className="d-flex justify-content-center">
                            <div 
                              className="bg-danger me-1" 
                              style={{ 
                                height: `${item.open * 20}px`, 
                                width: '10px'
                              }}
                            ></div>
                            <div 
                              className="bg-warning me-1" 
                              style={{ 
                                height: `${item.inProgress * 20}px`, 
                                width: '10px'
                              }}
                            ></div>
                            <div 
                              className="bg-success" 
                              style={{ 
                                height: `${item.resolved * 20}px`, 
                                width: '10px'
                              }}
                            ></div>
                          </div>
                          <div className="mt-2">
                            <small>Total: {item.open + item.inProgress + item.resolved}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-center text-muted">
                      <span className="badge bg-danger me-2">Open</span>
                      <span className="badge bg-warning me-2">In Progress</span>
                      <span className="badge bg-success">Resolved</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  // Render visitors report content
  const renderVisitorsReport = () => {
    if (!reportData) return null;
    
    const { 
      totalVisitors = 0, 
      activeVisitors = 0, 
      avgDailyVisitors = 0, 
      peakVisitHours = 'N/A',
      visitorsByPurpose = [],
      visitorsByUnit = [],
      monthlyVisitors = []
    } = reportData;
    
    return (
      <>
        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Total Visitors</h6>
                <h2 className="card-text">{totalVisitors}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Active Visitors</h6>
                <h2 className="card-text text-success">{activeVisitors}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Avg Daily Visitors</h6>
                <h2 className="card-text text-primary">{avgDailyVisitors}</h2>
              </div>
            </div>
          </div>
          
          <div className="col-md-3 mb-3">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="card-title text-muted p-3">Peak Visit Hours</h6>
                <h2 className="card-text text-info">{peakVisitHours}</h2>
              </div>
            </div>
          </div>
        </div>
        
        {/* Visitors Analysis */}
        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Visitors by Purpose</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Purpose</th>
                        <th>Count</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(visitorsByPurpose || []).map((purpose, index) => (
                        <tr key={index}>
                          <td>{purpose.purpose}</td>
                          <td>{purpose.count}</td>
                          <td>
                            {Math.round((purpose.count / totalVisitors) * 100)}%
                            <div className="progress mt-1" style={{ height: '6px' }}>
                              <div 
                                className="progress-bar bg-info" 
                                style={{ width: `${(purpose.count / totalVisitors) * 100}%` }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Top {unitPlural} by Visitors</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{formatDwellingUnit(settings)} Number</th>
                        <th>Visitor Count</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(visitorsByUnit || []).map((unit, index) => (
                        <tr key={index}>
                          <td>{formatDwellingUnit(settings, unit.unitNumber)}</td>
                          <td>{unit.count}</td>
                          <td>
                            {Math.round((unit.count / totalVisitors) * 100)}%
                            <div className="progress mt-1" style={{ height: '6px' }}>
                              <div 
                                className="progress-bar bg-primary" 
                                style={{ width: `${(unit.count / totalVisitors) * 100}%` }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Monthly Trend Chart (Placeholder) */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Monthly Visitors Trend</h5>
              </div>
              <div className="card-body">
                <div className="chart-container" style={{ height: '300px' }}>
                  {/* In a real implementation, this would be an actual chart component */}
                  <div className="placeholder-chart">
                    <div className="d-flex justify-content-between mb-4">
                      {(monthlyVisitors || []).map((item, index) => (
                        <div key={index} className="text-center">
                          <div className="mb-2">{item.month}</div>
                          <div 
                            className="bg-info rounded" 
                            style={{ 
                              height: `${item.count * 1}px`, 
                              width: '30px'
                            }}
                          ></div>
                          <div className="mt-2">{item.count}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-muted">Monthly Visitor Count</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  if (loading) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <PageTitle icon="fa-chart-bar" title="Reports & Data Export" />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle 
        icon="fa-chart-bar"
        title="Reports & Data Export"
        action={
          <Link to="/admin/dashboard" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>Back to Dashboard
          </Link>
        }
      />
      
      {/* Date Range Filter - Moved to top */}
      <div className="reportsDateRangeFilterCard card mb-4">
        <div className="card-body">
          <div className="row align-items-center flex-wrap">
            <div className="mb-2 mb-md-0 filterCardLeft">
              <div className="d-flex align-items-center flex-wrap date-range-filter-text">
                <i className="fas fa-calendar-alt text-primary me-2"></i>
                <span className="fw-bold text-primary me-2">Date Range Filter:</span>
                <small className="d-flex flex-wrap gap-2 text-muted">
                  Applies to time-based data (Visitors, Financials, Complaints). 
                  Current selection: <span className="badge bg-primary ms-1">{dateRange}</span>
                </small>
              </div>
            </div>
            <div className="filterCardRight">
              <div className="d-flex justify-content-md-end">
                <select 
                  className="form-select"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                  <option value="current_month">Current Month</option>
                  <option value="all">All Time</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-warning" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}
      
      {exportMessage && (
        <div className={`alert ${exportMessage.includes('success') ? 'alert-success' : 'alert-info'}`} role="alert">
          <i className={`fas ${exportMessage.includes('success') ? 'fa-check-circle' : 'fa-info-circle'} me-2`}></i>
          {exportMessage}
        </div>
      )}
      
      {!canExport && (
        <div className="alert alert-warning" role="alert">
          <i className="fas fa-lock me-2"></i>
          <strong>Access Restricted:</strong> Data export functionality is only available to Admin and SuperAdmin users.
        </div>
      )}
      
      {/* Data Export Section */}
      <div className="card mb-3">
        <div className="card-header" style={{ padding: '0.75rem 1rem', backgroundColor: 'transparent', border: 'none' }}>
          <h6 className="mb-0 fw-bold" style={{ color: settings.block_title_color || '#8B4513' }}>
            <i className="fas fa-download me-2"></i>
            Data Export Center
          </h6>
          <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>Export various data modules in CSV or PDF format</small>
        </div>
        <div className="card-body p-3">
          <div className="row g-2">
            <ExportSection
              title="Visitors Data"
              description="Export visitor logs with check-in/out times, photos, and unit information"
              dataType="visitors"
              icon="fas fa-users"
              color="info"
              exportLoading={exportLoading}
              handleExportData={handleExportData}
              canExport={canExport}
            />
            
            <ExportSection
              title="Financials & Transactions"
              description="Export financial records, payments, dues, and transaction history"
              dataType="financials"
              icon="fas fa-money-bill-wave"
              color="success"
              exportLoading={exportLoading}
              handleExportData={handleExportData}
              canExport={canExport}
            />
            
            <ExportSection
              title="Units Data"
              description={`Export ${unitPlural} information, occupancy status, and owner details`}
              dataType="units"
              icon="fas fa-home"
              color="primary"
              exportLoading={exportLoading}
              handleExportData={handleExportData}
              canExport={canExport}
            />
            
            <ExportSection
              title="Complaints"
              description="Export complaint logs, status updates, and resolution history"
              dataType="complaints"
              icon="fas fa-exclamation-circle"
              color="warning"
              exportLoading={exportLoading}
              handleExportData={handleExportData}
              canExport={canExport}
            />
            
            <ExportSection
              title="Directory (Residents & Staff)"
              description="Export contact directory including residents, owners, and staff information"
              dataType="directory"
              icon="fas fa-address-book"
              color="secondary"
              exportLoading={exportLoading}
              handleExportData={handleExportData}
              canExport={canExport}
            />
            
            <ExportSection
              title="Users Data"
              description="Export user accounts, roles, permissions, and access information"
              dataType="users"
              icon="fas fa-user-friends"
              color="dark"
              exportLoading={exportLoading}
              handleExportData={handleExportData}
              canExport={canExport}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports; 